/* Floor v2 — three.js low-poly office diorama.
   Ported from the approved prototype. All sample/demo logic removed.
   Public API mirrors floorEngine.js so Floor.jsx can swap engines with
   a single try/catch:
     createScene3D({ canvas, overlay, container, reducedMotion }) =>
       { start, stop, resize, snapshot, setAgents, enqueue, destroy }
*/
import * as THREE from "three";

/* ------------------------------------------------------------------ */
/* Status → Office design token (SVG colour comes from CSS).          */
const STATUS = {
  working:  { word: "Working",    hint: "At the desk on a task",       token: "--ok"     },
  idle:     { word: "Idle",       hint: "At the desk, nothing queued", token: "--muted"  },
  walking:  { word: "Walking",    hint: "Moving for a request",        token: "--cobalt" },
  meeting:  { word: "In meeting", hint: "In the meeting room",         token: "--violet" },
  blocked:  { word: "Blocked",    hint: "Waiting for an approval",     token: "--bad"    },
  offline:  { word: "Offline",    hint: "No heartbeat",                token: "--off"    },
};
function cssVar(name) {
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || "#888";
  } catch { return "#888"; }
}

/* Palette — Office is single dark theme; locked to dark values. */
const PAL = {
  floor:    0x232C48,
  corridor: 0x1A2238,
  wall:     0x35436E,
  desk:     0x6B5A44,
  deskTop:  0x84694D,
  screen:   0x0A0E1A,
  table:    0x6E5B45,
  edge:     0x7C95FF,
  label:    "#9DB1FF",
  ring:     0x7C95FF,
};

/* ------------------------------------------------------------------ */
/* Rooms + slot pool for lazy-spawned (unknown) agents.               */
const RW = 7.6, RD = 7, SPEED = 3.3, LANE = 0.65;
const ROOMS = [
  { id: "ceo",   name: "CEO Office",      x: -16, dir: -1 },
  { id: "prod",  name: "Product Studio",  x: -8,  dir: -1 },
  { id: "it",    name: "IT",              x: 0,   dir: -1 },
  { id: "mkt",   name: "Marketing",       x: 8,   dir: -1 },
  { id: "social",name: "Social Studio",   x: 16,  dir: -1, accent: 0x2EB8EF },
  { id: "sales", name: "Sales",           x: -16, dir: 1  },
  { id: "fin",   name: "Finance",         x: -8,  dir: 1  },
  { id: "legal", name: "Legal",           x: 0,   dir: 1  },
  { id: "meet",  name: "Meeting Room",    x: 8,   dir: 1  },
  { id: "afg",   name: "AFG Dubai",       x: 16,  dir: 1, accent: 0x244494, labelColor: 0x7C9BEA },
];
/* Department name (as returned by /api/office/live) → room id. */
const DEPT_TO_ROOM = {
  "CEO Office": "ceo",
  "Product Studio": "prod",
  "IT": "it",
  "Marketing": "mkt",
  "Social Studio": "social",
  "AFG Dubai": "afg",
  "Sales": "sales",
  "Finance": "fin",
  "Legal": "legal",
};
/* Hand-made agent looks keyed by canonical name. */
const DEFS = {
  "Chief of Staff":  { room: "ceo",  slot: [0, 5.4],  skin: 0xE0B08A, shirt: 0x1F3FD1, hair: "short",  hc: 0x2B2118, acc: "tie",     ac: 0xF2B544, item: "clip",    base: "working", act: "Routing today’s tasks" },
  "Design Lead":     { room: "prod", slot: [-2.4,5.6],skin: 0xF1C8A5, shirt: 0xF2B544, hair: "bun",    hc: 0x7A3B2E, acc: "scarf",   ac: 0xE8E8F0, item: "palette", base: "working", act: "Drafting the floor prototype" },
  "Dev Lead":        { room: "prod", slot: [0, 5.6],  skin: 0xC68E67, shirt: 0x2A3258, hair: "short",  hc: 0x111111, acc: "glasses", ac: 0x151A2B, item: "laptop",  base: "working", act: "Reviewing the build plan" },
  "Product":         { room: "prod", slot: [2.4,5.6], skin: 0xA8714B, shirt: 0x2A9D8F, hair: "spiky",  hc: 0x1E1A16, acc: "badge",   ac: 0xF2B544, item: "notes",   base: "working", act: "Sorting feature requests" },
  "QA":              { room: "prod", slot: [-1.2,3.6],skin: 0xF1C8A5, shirt: 0x8A5CC2, hair: "long",   hc: 0xC9A24A, acc: "glasses", ac: 0x151A2B, item: "bug",     base: "blocked", act: "Waiting for an approval" },
  "Release":         { room: "prod", slot: [1.2,3.6], skin: 0x7A4A2E, shirt: 0xE8E8F0, hair: "bald",   hc: 0x000000, acc: "hat",     ac: 0x1F3FD1, item: "rocket",  base: "offline", act: "No heartbeat for 12 min" },
  "IT Lead":         { room: "it",   slot: [0, 5.4],  skin: 0xE0B08A, shirt: 0x3A4A66, hair: "tall",   hc: 0x5A3A22, acc: "headset", ac: 0x151A2B, item: "server",  base: "working", act: "Checking mail intake" },
  "Marketing Lead":  { room: "mkt",  slot: [-1.6,5.4],skin: 0xF1C8A5, shirt: 0xE85D75, hair: "long",   hc: 0x2B2118, acc: "none",    ac: 0,        item: "mega",    base: "working", act: "Planning the QRY launch" },
  "SEO Lead":        { room: "mkt",  slot: [1.6, 5.4],skin: 0xC68E67, shirt: 0x5CA85C, hair: "short",  hc: 0x8A5A2B, acc: "glasses", ac: 0x151A2B, item: "lens",    base: "idle",    act: "Between tasks" },
  "Social Lead":     { room: "social",slot: [0, 5.4], skin: 0xC68E67, shirt: 0x2EB8EF, hair: "spiky",  hc: 0x151515, acc: "headset", ac: 0x151A2B, item: "phone",   base: "idle",    act: "Between tasks" },
  "AFG Lead Desk":   { room: "afg",   slot: [0, 5.4], skin: 0xB07A55, shirt: 0x244494, hair: "short",  hc: 0x2A1E16, acc: "headset", ac: 0x151A2B, item: "laptop",  base: "idle",    act: "Watching leads" },
  "Sales Lead":      { room: "sales",slot: [0, 5.4],  skin: 0xA8714B, shirt: 0xE07B39, hair: "short",  hc: 0x151515, acc: "headset", ac: 0x151A2B, item: "phone",   base: "working", act: "Qualifying a new lead" },
  "CFO":             { room: "fin",  slot: [0, 5.4],  skin: 0xE0B08A, shirt: 0x1B6B5F, hair: "bald",   hc: 0x000000, acc: "tie",     ac: 0xF2B544, item: "coins",   base: "idle",    act: "Between tasks" },
  "Legal Lead":      { room: "legal",slot: [0, 5.4],  skin: 0xF1C8A5, shirt: 0x4B3F72, hair: "bun",    hc: 0x303030, acc: "glasses", ac: 0x151A2B, item: "books",   base: "working", act: "Reading the terms draft" },
};
/* Deterministic unknown-agent resolution. */
const SKINS   = [0xE0B08A, 0xF1C8A5, 0xC68E67, 0xA8714B, 0x7A4A2E];
const HAIRS   = ["short", "bun", "long", "tall", "spiky", "bald"];
const HCS     = [0x2B2118, 0x111111, 0x7A3B2E, 0xC9A24A, 0x5A3A22, 0x151515];
const ACCS    = ["none", "glasses", "headset", "tie", "hat", "scarf", "badge"];
const ACS     = [0xF2B544, 0xE8E8F0, 0x1F3FD1, 0x151A2B, 0x3A4A66];
const SHIRT_BY_DEPT = {
  "CEO Office": 0x1F3FD1, "Product Studio": 0x2A3258, "IT": 0x3A4A66,
  "Marketing": 0xE85D75, "Social Studio": 0x2EB8EF, "AFG Dubai": 0x244494, "Sales": 0xE07B39, "Finance": 0x1B6B5F, "Legal": 0x4B3F72,
};
const ITEM_BY_DEPT = {
  "CEO Office": "clip", "Product Studio": "laptop", "IT": "server",
  "Marketing": "mega", "Social Studio": "phone", "AFG Dubai": "laptop", "Sales": "phone", "Finance": "coins", "Legal": "books",
};
function hashStr(s) { let h = 2166136261 >>> 0; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = (h * 16777619) >>> 0; } return h; }

/* Slot pool: 6 desks per non-meeting room, 2 rows × 3 cols. */
function slotPool() {
  const out = {};
  ROOMS.forEach((r) => {
    if (r.id === "meet") return;
    const rows = [5.6, 3.6]; const cols = [-2.4, 0, 2.4];
    out[r.id] = [];
    rows.forEach((z) => cols.forEach((x) => out[r.id].push({ xy: [x, z], used: false })));
  });
  return out;
}

/* ------------------------------------------------------------------ */
export function createScene3D({ canvas, overlay, container, reducedMotion = false }) {
  if (!canvas || !overlay || !container) throw new Error("scene3d: missing canvas/overlay/container");
  const reduce = !!reducedMotion;

  /* Lock canvas + overlay to the container's box; prevents the canvas
     attribute size from stretching the grid cell. Also stretch the
     wrapping .fl-scene element so inset:0 resolves to the full stage. */
  const scenePar = canvas.parentElement;
  if (scenePar) {
    scenePar.style.position = "absolute";
    scenePar.style.inset = "0";
  }
  canvas.style.position = "absolute";
  canvas.style.inset = "0";
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  overlay.style.position = "absolute";
  overlay.style.inset = "0";
  overlay.style.pointerEvents = "none";

  /* Renderer. */
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.setClearColor(0x000000, 0);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(32, 1.6, 1, 200);
  const target = new THREE.Vector3(0, 0, 0.4);
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8892b0, 0.78));
  const sun = new THREE.DirectionalLight(0xffffff, 0.72);
  sun.position.set(-14, 26, 16); sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  const sc = sun.shadow.camera;
  sc.left = -24; sc.right = 24; sc.top = 18; sc.bottom = -18; sc.near = 1; sc.far = 70;
  sun.shadow.bias = -0.0006;
  scene.add(sun);

  /* Material registries (needed for disposal). */
  const reg = {};
  const regLine = [];
  const regAgent = []; /* per-agent material lists for disposal */
  const regExtra = []; /* role items, meeting, ring, markers, etc */
  const fixedCache = {};
  const ledMeshes = [];
  const labels = [];
  const edgeLines = [];
  const markerTex = {};

  function M(key) {
    const m = new THREE.MeshStandardMaterial({ color: PAL[key], flatShading: true, roughness: 0.92, metalness: 0 });
    (reg[key] = reg[key] || []).push(m); return m;
  }
  function F(c) {
    if (!fixedCache[c]) {
      const m = new THREE.MeshStandardMaterial({ color: c, flatShading: true, roughness: 0.92, metalness: 0 });
      fixedCache[c] = m; regExtra.push(m);
    }
    return fixedCache[c];
  }
  function box(w, h, d, mat, x, y, z, parent, shadow) {
    const g = new THREE.BoxGeometry(w, h, d);
    const m = new THREE.Mesh(g, mat);
    m.position.set(x || 0, y || 0, z || 0);
    if (shadow !== false) m.castShadow = true;
    m.receiveShadow = true;
    if (parent) parent.add(m);
    return m;
  }
  function cyl(rt, rb, h, seg, mat, x, y, z, parent) {
    const g = new THREE.CylinderGeometry(rt, rb, h, seg || 6);
    const m = new THREE.Mesh(g, mat);
    m.position.set(x || 0, y || 0, z || 0);
    m.castShadow = true;
    if (parent) parent.add(m);
    return m;
  }

  const RM = {};
  ROOMS.forEach((r) => { r.doorZ = r.dir < 0 ? -2 : 2; RM[r.id] = r; });
  function wz(r, d) { return r.doorZ + r.dir * d; }

  /* Rooms (floor, edges, walls, label canvas). */
  ROOMS.forEach((r) => {
    const g = new THREE.Group(); scene.add(g);
    const cz = wz(r, RD / 2);
    const floor = box(RW, 0.3, RD, M("floor"), r.x, -0.15, cz, g, false);
    floor.receiveShadow = true;
    const eg = new THREE.EdgesGeometry(new THREE.BoxGeometry(RW, 0.3, RD));
    const lm = new THREE.LineBasicMaterial({ color: r.accent || PAL.edge }); regLine.push(lm);
    const ls = new THREE.LineSegments(eg, lm);
    ls.position.set(r.x, -0.15, cz); g.add(ls);
    edgeLines.push({ geo: eg, total: eg.attributes.position.count });
    const wallH = r.dir < 0 ? 1.3 : 0.6;
    box(0.18, wallH, RD, M("wall"), r.x - RW / 2 + 0.09, wallH / 2, cz, g);
    box(0.18, wallH, RD, M("wall"), r.x + RW / 2 - 0.09, wallH / 2, cz, g);
    if (r.dir < 0) box(RW, wallH, 0.18, M("wall"), r.x, wallH / 2, wz(r, RD) - r.dir * 0.09, g);
    const c = document.createElement("canvas"); c.width = 512; c.height = 128;
    const tex = new THREE.CanvasTexture(c); tex.anisotropy = 4;
    const labMat = new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false });
    regExtra.push(labMat);
    const lab = new THREE.Mesh(new THREE.PlaneGeometry(5.2, 1.3), labMat);
    lab.rotation.x = -Math.PI / 2;
    lab.position.set(r.x - RW / 2 + 3.0, 0.02, wz(r, 0.95));
    g.add(lab);
    const lc = r.labelColor || r.accent;
    labels.push({ c, tex, text: r.name, color: lc ? "#" + lc.toString(16).padStart(6, "0") : null });
  });
  box(44, 0.2, 4, M("corridor"), 0, -0.1, 0, scene, false);
  function drawLabels() {
    labels.forEach((l) => {
      const x = l.c.getContext("2d");
      x.clearRect(0, 0, 512, 128);
      x.fillStyle = l.color || PAL.label;
      x.font = '700 76px "IBM Plex Sans",system-ui,sans-serif';
      x.textBaseline = "middle"; x.textAlign = "left";
      x.fillText(l.text, 10, 64, 500);
      l.tex.needsUpdate = true;
    });
  }
  drawLabels();
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(drawLabels).catch(() => {});

  /* Meeting table. */
  (function () {
    const r = RM.meet, dc = 3.8;
    const t = cyl(1.15, 1.15, 0.1, 10, M("table"), r.x, 0.78, wz(r, dc), scene); t.receiveShadow = true;
    cyl(0.18, 0.3, 0.74, 6, M("desk"), r.x, 0.37, wz(r, dc), scene);
    box(0.6, 0.03, 0.4, F(0xFFFFFF), r.x, 0.85, wz(r, dc) + 0.1, scene, false);
  })();

  /* Role items. */
  function monitorOn(parent, x, y, z) {
    box(0.72, 0.46, 0.06, M("screen"), x, y, z, parent);
    box(0.12, 0.16, 0.1, M("screen"), x, y - 0.3, z, parent);
  }
  function roleItem(kind, p) {
    const g = new THREE.Group(); p.add(g);
    if (kind === "coins") { [0, 1, 2].forEach((i) => cyl(0.15, 0.15, 0.06, 8, F(0xF2B544), 0, 0.03 + i * 0.065, 0, g)); cyl(0.15, 0.15, 0.06, 8, F(0xF2B544), 0.3, 0.03, 0.05, g); }
    else if (kind === "clip") { box(0.34, 0.03, 0.46, F(0xFFFFFF), 0, 0.02, 0, g); box(0.16, 0.05, 0.05, F(0x151A2B), 0, 0.045, -0.2, g); box(0.14, 0.04, 0.26, F(0x1F3FD1), 0.38, 0.02, 0.05, g); }
    else if (kind === "palette") {
      cyl(0.24, 0.24, 0.04, 10, F(0xF3EBDD), 0, 0.02, 0, g);
      [[F(0xE85D75), -0.1, -0.06], [F(0x1F3FD1), 0.04, -0.12], [F(0xF2B544), 0.12, 0.04]].forEach((d) => cyl(0.05, 0.05, 0.06, 6, d[0], d[1], 0.06, d[2], g));
    }
    else if (kind === "laptop") { box(0.5, 0.04, 0.34, F(0x9AA5C0), 0, 0.02, 0.05, g); const s = box(0.5, 0.34, 0.03, F(0x151A2B), 0, 0.2, -0.12, g); s.rotation.x = -0.25; }
    else if (kind === "server") {
      box(0.3, 0.62, 0.3, F(0x2A3258), 0, 0.31, 0, g);
      box(0.22, 0.05, 0.02, F(0x0F1424), 0, 0.5, 0.16, g);
      box(0.22, 0.05, 0.02, F(0x0F1424), 0, 0.38, 0.16, g);
      const led = box(0.05, 0.05, 0.03, F(0x3FD1B8), 0.08, 0.5, 0.17, g); led.userData.led = true; ledMeshes.push(led);
    }
    else if (kind === "books") { box(0.4, 0.1, 0.28, F(0x1F3FD1), 0, 0.05, 0, g); box(0.36, 0.09, 0.26, F(0xE85D75), 0.01, 0.145, 0, g); box(0.32, 0.08, 0.24, F(0xF2B544), -0.01, 0.23, 0, g); }
    else if (kind === "mega") { const c1 = cyl(0.04, 0.2, 0.34, 6, F(0xE85D75), 0, 0.2, 0, g); c1.rotation.z = Math.PI / 2; box(0.14, 0.14, 0.1, F(0x151A2B), -0.2, 0.12, 0, g); }
    else if (kind === "lens") {
      const trg = new THREE.TorusGeometry(0.15, 0.03, 5, 10);
      const trMat = F(0x151A2B);
      const tr = new THREE.Mesh(trg, trMat); tr.position.set(0, 0.28, 0); tr.castShadow = true; g.add(tr);
      const h = cyl(0.025, 0.025, 0.26, 5, trMat, 0.13, 0.1, 0, g); h.rotation.z = 0.8;
    }
    else if (kind === "phone") { box(0.22, 0.05, 0.34, F(0x151A2B), 0, 0.03, 0, g); box(0.1, 0.04, 0.2, F(0x3A4A66), 0, 0.06, -0.02, g); cyl(0.03, 0.03, 0.3, 5, F(0x151A2B), 0.28, 0.05, 0, g).rotation.z = Math.PI / 2; }
    else if (kind === "notes") { [[0xF2E27A, -0.14, 0], [0xF29BB0, 0.02, 0.06], [0x8FC9F2, 0.18, -0.04]].forEach((n, i) => box(0.2, 0.025, 0.2, F(n[0]), n[1], 0.015 + i * 0.003, n[2], g)); }
    else if (kind === "bug") {
      const b = new THREE.Mesh(new THREE.IcosahedronGeometry(0.14, 0), F(0xD2553F));
      b.position.y = 0.16; b.castShadow = true; g.add(b);
      [-1, 1].forEach((s) => [-0.08, 0, 0.08].forEach((z) => { const l = cyl(0.01, 0.01, 0.16, 3, F(0x151A2B), s * 0.17, 0.1, z, g); l.rotation.z = s * 0.9; }));
    }
    else if (kind === "rocket") {
      cyl(0.1, 0.1, 0.34, 7, F(0xE8E8F0), 0, 0.25, 0, g);
      cyl(0, 0.1, 0.16, 7, F(0xD2553F), 0, 0.5, 0, g);
      box(0.22, 0.1, 0.02, F(0x1F3FD1), 0, 0.12, 0, g);
      box(0.02, 0.1, 0.22, F(0x1F3FD1), 0, 0.12, 0, g);
    }
    return g;
  }

  /* Marker sprite texture (uses live Office tokens). */
  function markerTexture(s) {
    const key = s;
    if (markerTex[key]) return markerTex[key];
    const c = document.createElement("canvas"); c.width = c.height = 64;
    const x = c.getContext("2d");
    const col = cssVar(STATUS[s].token);
    x.lineJoin = "round"; x.lineWidth = 7; x.strokeStyle = "#ffffff"; x.fillStyle = col;
    x.beginPath();
    if (s === "working") { x.arc(32, 32, 17, 0, 7); x.stroke(); x.fill(); }
    else if (s === "idle") { x.arc(32, 32, 14, 0, 7); x.lineWidth = 9; x.strokeStyle = "#fff"; x.stroke(); x.lineWidth = 5; x.strokeStyle = col; x.stroke(); }
    else if (s === "walking") { x.moveTo(32, 12); x.lineTo(53, 50); x.lineTo(11, 50); x.closePath(); x.stroke(); x.fill(); }
    else if (s === "meeting") { x.rect(15, 15, 34, 34); x.stroke(); x.fill(); }
    else if (s === "blocked") { x.moveTo(32, 8); x.lineTo(56, 32); x.lineTo(32, 56); x.lineTo(8, 32); x.closePath(); x.stroke(); x.fill(); }
    else { x.rect(8, 26, 48, 12); x.stroke(); x.fill(); }
    const t = new THREE.CanvasTexture(c); markerTex[key] = t; return t;
  }

  /* Agent builder. */
  function buildAgent(def) {
    const g = new THREE.Group(); const mats = [];
    const mm = (c) => { const m = new THREE.MeshStandardMaterial({ color: c, flatShading: true, roughness: 0.92, metalness: 0 }); mats.push(m); return m; };
    const skin = mm(def.skin), shirt = mm(def.shirt), pants = mm(0x2A3252),
          hairM = mm(def.hc), dark = mm(0x151A2B), acc = mm(def.ac || 0x151A2B);
    const legL = new THREE.Group(), legR = new THREE.Group();
    legL.position.set(-0.14, 0.5, 0); legR.position.set(0.14, 0.5, 0);
    box(0.18, 0.5, 0.2, pants, 0, -0.25, 0, legL); box(0.18, 0.5, 0.2, pants, 0, -0.25, 0, legR);
    g.add(legL); g.add(legR);
    cyl(0.3, 0.36, 0.62, 6, shirt, 0, 0.81, 0, g);
    const armL = new THREE.Group(), armR = new THREE.Group();
    armL.position.set(-0.42, 1.08, 0); armR.position.set(0.42, 1.08, 0);
    box(0.13, 0.5, 0.13, shirt, 0, -0.25, 0, armL); box(0.13, 0.12, 0.13, skin, 0, -0.54, 0, armL);
    box(0.13, 0.5, 0.13, shirt, 0, -0.25, 0, armR); box(0.13, 0.12, 0.13, skin, 0, -0.54, 0, armR);
    g.add(armL); g.add(armR);
    const head = new THREE.Group(); head.position.set(0, 1.42, 0); g.add(head);
    const hm = new THREE.Mesh(new THREE.IcosahedronGeometry(0.28, 0), skin); hm.castShadow = true; head.add(hm);
    box(0.06, 0.08, 0.04, dark, -0.1, 0.03, 0.25, head, false);
    box(0.06, 0.08, 0.04, dark,  0.1, 0.03, 0.25, head, false);
    const h = def.hair;
    if (h === "short" || h === "bun" || h === "long") {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.3, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), hairM);
      cap.position.y = 0.04; cap.castShadow = true; head.add(cap);
      if (h === "bun") { const bn = new THREE.Mesh(new THREE.SphereGeometry(0.12, 5, 4), hairM); bn.position.set(0, 0.38, -0.05); head.add(bn); }
      if (h === "long") box(0.52, 0.5, 0.14, hairM, 0, -0.14, -0.2, head);
    } else if (h === "tall") { box(0.42, 0.22, 0.42, hairM, 0, 0.33, 0, head); }
    else if (h === "spiky") { [-0.13, 0, 0.13].forEach((xx) => { const c = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.3, 4), hairM); c.position.set(xx, 0.38, 0); c.castShadow = true; head.add(c); }); }
    const a = def.acc;
    if (a === "glasses") { [-0.1, 0.1].forEach((xx) => { const t = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.018, 4, 8), dark); t.position.set(xx, 0.03, 0.27); head.add(t); }); box(0.06, 0.02, 0.02, dark, 0, 0.04, 0.27, head, false); }
    else if (a === "headset") { const t2 = new THREE.Mesh(new THREE.TorusGeometry(0.3, 0.025, 4, 10, Math.PI), dark); t2.position.y = 0.02; head.add(t2); box(0.08, 0.16, 0.1, dark, -0.3, 0, 0, head); box(0.08, 0.16, 0.1, dark, 0.3, 0, 0, head); box(0.04, 0.04, 0.16, dark, 0.24, -0.12, 0.1, head); }
    else if (a === "tie") { box(0.1, 0.3, 0.04, acc, 0, -0.43, 0.34, head); }
    else if (a === "hat") { cyl(0.34, 0.34, 0.06, 8, acc, 0, 0.28, 0, head); cyl(0.2, 0.22, 0.18, 8, acc, 0, 0.39, 0, head); }
    else if (a === "scarf") { const t3 = new THREE.Mesh(new THREE.TorusGeometry(0.27, 0.07, 4, 8), acc); t3.rotation.x = Math.PI / 2; t3.position.y = -0.3; head.add(t3); }
    else if (a === "badge") { box(0.1, 0.13, 0.03, acc, -0.14, -0.42, 0.34, head); }
    g.traverse((o) => { if (o.isMesh) o.castShadow = true; });
    regAgent.push(mats);
    return { g, mats, legL, legR, armL, armR, head };
  }

  /* Slot pool for unknown agents. */
  const slots = slotPool();
  function takeSlot(roomId) {
    const pool = slots[roomId];
    if (!pool) return null;
    const free = pool.find((p) => !p.used);
    if (free) { free.used = true; return free.xy; }
    return null;
  }
  function takeAnySlot() {
    for (const id of Object.keys(slots)) {
      const xy = takeSlot(id);
      if (xy) return { roomId: id, slot: xy };
    }
    return null;
  }

  /* Resolve an API agent payload to a def used by buildAgent. */
  function resolveDef(name, department) {
    const canon = DEFS[name];
    if (canon) return { ...canon, _canonical: true };
    const h = hashStr(name || "unknown");
    const roomId = DEPT_TO_ROOM[department] || "prod";
    let xy = takeSlot(roomId);
    let fallbackRoom = roomId;
    if (!xy) {
      const alt = takeAnySlot();
      if (alt) { xy = alt.slot; fallbackRoom = alt.roomId; }
      else { xy = [0, 5.4]; fallbackRoom = roomId; }
    }
    return {
      room: fallbackRoom,
      slot: xy,
      skin: SKINS[h % SKINS.length],
      shirt: SHIRT_BY_DEPT[department] || 0x56627a,
      hair: HAIRS[(h >> 3) % HAIRS.length],
      hc: HCS[(h >> 7) % HCS.length],
      acc: ACCS[(h >> 11) % ACCS.length],
      ac: ACS[(h >> 15) % ACS.length],
      item: ITEM_BY_DEPT[department] || "notes",
      base: "idle",
      act: "",
      _canonical: false,
    };
  }

  /* ------------------------------------------------------------------
     Agents (name-keyed) + runtime state.
  ------------------------------------------------------------------ */
  const AG = {};
  const AGL = [];
  const hits = [];

  function makeNodes(a) {
    const r = RM[a.def.room], d = a.def.slot[1];
    a.home  = { room: r, pt: new THREE.Vector3(r.x + a.def.slot[0], 0, wz(r, d - 1.0)) };
    a.visit = { room: r, pt: new THREE.Vector3(r.x + a.def.slot[0], 0, wz(r, d - 2.1)) };
    a.deskPos = new THREE.Vector3(r.x + a.def.slot[0], 0, wz(r, d));
  }
  function pushDesk(a) {
    const r = RM[a.def.room];
    const desk = new THREE.Group();
    desk.position.copy(a.deskPos);
    desk.rotation.y = r.dir < 0 ? 0 : Math.PI;
    scene.add(desk);
    box(1.6, 0.1, 0.9, M("deskTop"), 0, 0.78, 0, desk);
    box(0.1, 0.73, 0.8, M("desk"), -0.7, 0.365, 0, desk);
    box(0.1, 0.73, 0.8, M("desk"),  0.7, 0.365, 0, desk);
    monitorOn(desk, -0.35, 1.12, -0.2);
    const it = roleItem(a.def.item, desk); it.position.set(0.45, 0.83, 0);
    a.deskGroup = desk;
  }
  function markSlotUsed(def) {
    const pool = slots[def.room]; if (!pool) return;
    const hit = pool.find((p) => p.xy[0] === def.slot[0] && p.xy[1] === def.slot[1]);
    if (hit) hit.used = true;
  }
  function spawnAgent(name, department) {
    if (AG[name]) return AG[name];
    const def = resolveDef(name, department);
    markSlotUsed(def);
    const a = {
      def, id: name, name, department: department || RM[def.room]?.name || "",
      q: [], cur: null, bubble: null, bubbleEl: null,
      status: def.base, rotT: 0, phase: Math.random() * 6,
      activity: def.act || "",
    };
    const b = buildAgent(def);
    a.parts = b; a.g = b.g; a.mats = b.mats;
    makeNodes(a); pushDesk(a);
    a.faceY = RM[def.room].dir < 0 ? Math.PI : 0;
    a.g.position.copy(a.home.pt); a.g.rotation.y = a.faceY; a.rotT = a.faceY;
    scene.add(a.g);
    const spMat = new THREE.SpriteMaterial({ map: markerTexture(def.base), depthTest: false, transparent: true });
    regExtra.push(spMat);
    const sp = new THREE.Sprite(spMat);
    sp.scale.set(0.7, 0.7, 0.7); sp.position.set(0, 2.25, 0); sp.renderOrder = 5;
    a.g.add(sp); a.marker = sp;
    const hitMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false });
    regExtra.push(hitMat);
    const hit = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.55, 2.2, 8), hitMat);
    hit.position.y = 1.1; hit.userData.agent = a;
    a.g.add(hit); a.hit = hit; hits.push(hit);
    AG[name] = a; AGL.push(a);
    return a;
  }

  /* Hover ring. */
  const ringMat = new THREE.MeshBasicMaterial({ color: PAL.ring, transparent: true, opacity: 0.9, side: THREE.DoubleSide });
  regExtra.push(ringMat);
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.58, 0.74, 28), ringMat);
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.04; ring.visible = false;
  scene.add(ring);

  /* Meeting seats. */
  const SEATS = [];
  (function () {
    const r = RM.meet, dc = 3.8, R = 1.75, RV = 2.75, phis = [-150, -90, -30, 30, 90, 150];
    phis.forEach((deg) => {
      const p = deg * Math.PI / 180;
      const seat = new THREE.Vector3(r.x + R * Math.sin(p), 0, wz(r, dc - R * Math.cos(p)));
      const via = []; const step = 30; const sign = deg < 0 ? -1 : 1; let cur = 0;
      while (Math.abs(cur) < Math.abs(deg)) {
        const q = cur * Math.PI / 180;
        via.push(new THREE.Vector3(r.x + RV * Math.sin(q), 0, wz(r, dc - RV * Math.cos(q))));
        cur += sign * step;
      }
      SEATS.push({ room: r, pt: seat, via, center: new THREE.Vector3(r.x, 0, wz(r, dc)), used: false });
    });
  })();

  /* Routing. */
  function route(s, e) {
    const pts = [];
    if (s.room === e.room && !s.via && !e.via) {
      const az = wz(s.room, 1.4);
      pts.push(new THREE.Vector3(s.pt.x, 0, az), new THREE.Vector3(e.pt.x, 0, az), e.pt.clone());
      return pts;
    }
    let sx = s.pt.x;
    if (s.via && s.via.length) {
      for (let i = s.via.length - 1; i >= 0; i--) pts.push(s.via[i].clone());
      sx = s.via[0].x;
    }
    pts.push(new THREE.Vector3(sx, 0, s.room.doorZ));
    const ex = e.via && e.via.length ? e.via[0].x : e.pt.x;
    const lz = ex >= sx ? LANE : -LANE;
    if (Math.abs(ex - sx) > 0.01 || s.room !== e.room) {
      pts.push(new THREE.Vector3(sx, 0, lz), new THREE.Vector3(ex, 0, lz));
    }
    pts.push(new THREE.Vector3(ex, 0, e.room.doorZ));
    if (e.via) e.via.forEach((v) => pts.push(v.clone()));
    pts.push(e.pt.clone());
    return pts;
  }
  function pathLen(from, pts) {
    let L = 0, p = from.clone();
    pts.forEach((q) => { L += Math.hypot(q.x - p.x, q.z - p.z); p = q.clone(); });
    return L;
  }

  /* Runtime. */
  let T = 0;
  function setStatus(a, s) {
    if (!STATUS[s]) s = "idle";
    a.status = s;
    a.marker.material.map = markerTexture(s);
    a.marker.material.needsUpdate = true;
    const off = s === "offline";
    a.mats.forEach((m) => { m.transparent = off; m.opacity = off ? 0.42 : 1; });
  }
  function initAction(a, c) {
    if (c.t === "walk") {
      setStatus(a, "walking");
      if (reduce) {
        const last = c.pts[c.pts.length - 1];
        if (last) { a.g.position.set(last.x, 0, last.z); }
        a.cur = { t: "wait", left: 0.4 };
      }
    } else if (c.t === "say") { a.bubble = c.text; c.left = c.ttl; }
    else if (c.t === "wait")  { c.left = c.dur; }
    else if (c.t === "face")  { a.rotT = Math.atan2(c.x - a.g.position.x, c.z - a.g.position.z); c.left = 0.35; }
    else if (c.t === "status"){ setStatus(a, c.s); a.cur = null; }
    else if (c.t === "call")  { try { c.fn(); } catch {} a.cur = null; }
  }
  function finish(a) { if (a.cur && a.cur.t === "say") a.bubble = null; a.cur = null; }
  function stepAgent(a, dt) {
    let guard = 0;
    while (!a.cur && a.q.length && guard++ < 8) { a.cur = a.q.shift(); initAction(a, a.cur); }
    const c = a.cur; let moving = false;
    if (c) {
      if (c.t === "walk") {
        if (!c.pts.length) { finish(a); }
        else {
          const tg = c.pts[0], p = a.g.position;
          const dx = tg.x - p.x, dz = tg.z - p.z, d = Math.hypot(dx, dz), st = SPEED * dt;
          if (d <= st) { p.x = tg.x; p.z = tg.z; c.pts.shift(); }
          else { p.x += dx / d * st; p.z += dz / d * st; a.rotT = Math.atan2(dx, dz); }
          moving = true; a.phase += dt * 9;
        }
      } else if (c.t === "until") {
        const at = c.ref ? c.ref.at : c.at;
        if (T >= at) finish(a);
      } else {
        c.left -= dt; if (c.left <= 0) finish(a);
      }
    }
    let diff = a.rotT - a.g.rotation.y;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    a.g.rotation.y += reduce ? diff : diff * Math.min(1, dt * 10);
    a.moving = moving;
  }
  function pose(a, t) {
    const p = a.parts, s = a.status;
    p.armL.rotation.x = p.armR.rotation.x = 0;
    p.legL.rotation.x = p.legR.rotation.x = 0;
    p.head.rotation.set(0, 0, 0);
    a.g.position.y = 0;
    const k = a.moving ? "walk" : s;
    let sw;
    if (k === "walk") {
      sw = reduce ? 0 : Math.sin(a.phase);
      p.legL.rotation.x = sw * 0.7; p.legR.rotation.x = -sw * 0.7;
      p.armL.rotation.x = -sw * 0.6; p.armR.rotation.x = sw * 0.6;
      a.g.position.y = reduce ? 0 : Math.abs(Math.sin(a.phase)) * 0.07;
    } else if (s === "working") {
      p.armL.rotation.x = -1.05 + (reduce ? 0 : Math.sin(t * 11 + a.phase) * 0.09);
      p.armR.rotation.x = -1.05 + (reduce ? 0 : Math.sin(t * 11 + a.phase + 2) * 0.09);
      p.head.rotation.x = 0.12;
    } else if (s === "meeting") {
      p.armR.rotation.x = reduce ? -0.5 : -0.5 + Math.sin(t * 2 + a.phase) * 0.45;
      p.head.rotation.y = reduce ? 0 : Math.sin(t * 1.2 + a.phase) * 0.3;
    } else if (s === "blocked") {
      p.armL.rotation.x = -2.7; p.armR.rotation.x = -2.7;
      p.head.rotation.y = reduce ? 0 : Math.sin(t * 2.2) * 0.35;
    } else if (s === "offline") {
      p.head.rotation.x = 0.55; p.armL.rotation.x = 0.1; p.armR.rotation.x = 0.1;
    } else {
      p.head.rotation.y = reduce ? 0 : Math.sin(t * 0.7 + a.phase) * 0.25;
      a.g.position.y = reduce ? 0 : Math.sin(t * 1.6 + a.phase) * 0.015;
    }
    const m = a.marker; let scl = 0.7;
    if (s === "blocked" && !reduce) scl = 0.7 + Math.sin(t * 5) * 0.1;
    m.scale.set(scl, scl, scl);
    m.position.y = 2.25 + (reduce ? 0 : Math.sin(t * 2 + a.phase) * 0.04);
  }

  /* ------------------------------------------------------------------
     Event dispatcher (requests, meetings) + back-pressure.
  ------------------------------------------------------------------ */
  const activeMeetings = {}; /* taskId -> { ref, participants: [a] } */
  function queuedWalks() {
    let n = 0; for (const a of AGL) for (const c of a.q) if (c.t === "walk") n++;
    return n;
  }
  function free(a) {
    if (!a) return false;
    if (a.cur || a.q.length) return false;
    return a.status !== "meeting";
  }
  function startRequest(fromName, toName, text) {
    const a = AG[fromName], b = AG[toName];
    if (!a || !b) return;
    if (a.q.length > 10 || b.q.length > 10) return;
    const overload = queuedWalks() > 6;
    if (!overload) {
      const pts = route(a.home, b.visit);
      a.q.push({ t: "walk", pts });
    }
    a.q.push({ t: "say", text: text || "…", ttl: 2.2 });
    a.q.push({ t: "wait", dur: 1.5 });
    if (!overload) {
      const back = route(b.visit, a.home);
      a.q.push({ t: "walk", pts: back });
      a.q.push({ t: "face", x: a.deskPos.x, z: a.deskPos.z });
    }
    a.q.push({ t: "status", s: a.def.base || "working" });
    b.q.push({ t: "wait", dur: 1.2 });
  }
  function startReply(fromName, text) {
    const a = AG[fromName]; if (!a) return;
    a.q.push({ t: "say", text: text || "…", ttl: 2.2 });
  }
  function clearSeatUsage() { SEATS.forEach((s) => (s.used = false)); }
  function startMeeting(participantNames, taskId) {
    if (activeMeetings[taskId]) return;
    clearSeatUsage();
    const people = [];
    const routes = [];
    participantNames.forEach((n) => {
      const a = AG[n]; if (!a) return;
      if (people.length < 6 && free(a)) {
        const seat = SEATS.find((s) => !s.used); if (!seat) return;
        seat.used = true;
        people.push(a);
        routes.push({ a, seat, pts: route(a.home, { room: seat.room, pt: seat.pt, via: seat.via }) });
      } else {
        /* over capacity or busy — mark meeting via status only. */
        if (a) a.q.push({ t: "status", s: "meeting" });
      }
    });
    if (!people.length) return;
    const ref = { at: T + 90 }; /* 90s safety timeout */
    activeMeetings[taskId] = { ref, participants: people };
    const overload = queuedWalks() > 6;
    routes.forEach((r) => {
      const a = r.a;
      if (!overload) a.q.push({ t: "walk", pts: r.pts });
      a.q.push({ t: "face", x: r.seat.center.x, z: r.seat.center.z });
      a.q.push({ t: "status", s: "meeting" });
      a.q.push({ t: "until", ref });
      const back = route({ room: r.seat.room, pt: r.seat.pt, via: r.seat.via }, a.home);
      if (!overload) a.q.push({ t: "walk", pts: back });
      a.q.push({ t: "face", x: a.deskPos.x, z: a.deskPos.z });
      a.q.push({ t: "status", s: a.def.base || "working" });
    });
  }
  function endMeeting(taskId) {
    const m = activeMeetings[taskId]; if (!m) return;
    m.ref.at = T + 0.1; delete activeMeetings[taskId];
  }

  /* ------------------------------------------------------------------
     Public API.
  ------------------------------------------------------------------ */
  function setAgents(agents) {
    if (!Array.isArray(agents)) return;
    const seen = new Set();
    agents.forEach((apay) => {
      const name = apay && apay.name; if (!name) return;
      seen.add(name);
      let a = AG[name];
      if (!a) a = spawnAgent(name, apay.department);
      a.department = apay.department || a.department;
      a.activity = apay.activity || a.activity;
      a.task = apay.task || null;
      if (!a.cur && !a.q.length && STATUS[apay.status]) setStatus(a, apay.status);
    });
    /* Agents missing in this payload → offline (do not dispose). */
    AGL.forEach((a) => {
      if (!seen.has(a.name) && a.status !== "offline" && !a.cur && !a.q.length) {
        setStatus(a, "offline");
      }
    });
  }
  function snapshot(agents) {
    if (!Array.isArray(agents)) return;
    /* Reset queues/bubbles. */
    AGL.forEach((a) => {
      a.q.length = 0; a.cur = null; a.bubble = null;
      if (a.bubbleEl) { a.bubbleEl.style.display = "none"; }
    });
    Object.keys(activeMeetings).forEach((k) => delete activeMeetings[k]);
    clearSeatUsage();
    /* Place agents, materialising unknown ones first. */
    agents.forEach((apay) => {
      const name = apay && apay.name; if (!name) return;
      const a = AG[name] || spawnAgent(name, apay.department);
      a.department = apay.department || a.department;
      a.activity = apay.activity || a.activity;
      if (apay.status === "meeting") {
        const seat = SEATS.find((s) => !s.used);
        if (seat) { seat.used = true; a.g.position.set(seat.pt.x, 0, seat.pt.z); a.rotT = Math.atan2(seat.center.x - seat.pt.x, seat.center.z - seat.pt.z); a.g.rotation.y = a.rotT; }
        setStatus(a, "meeting");
      } else {
        a.g.position.copy(a.home.pt); a.rotT = a.faceY; a.g.rotation.y = a.faceY;
        setStatus(a, apay.status === "walking" ? "working" : (STATUS[apay.status] ? apay.status : "idle"));
      }
    });
  }
  function enqueue(events) {
    if (!Array.isArray(events)) return;
    events.forEach((ev) => {
      if (!ev || !ev.type) {
        /* Pre-v1.4 chat shape: {from,to,text}. */
        if (ev && ev.from && ev.to && AG[ev.from] && AG[ev.to]) startRequest(ev.from, ev.to, ev.text || "");
        return;
      }
      if (ev.type === "meeting_start") {
        const names = Array.isArray(ev.agents) ? ev.agents : [];
        const tid = ev.task && ev.task.id ? ev.task.id : `m-${ev.seq || Date.now()}`;
        startMeeting(names, tid);
      } else if (ev.type === "meeting_end") {
        const tid = ev.task && ev.task.id;
        if (tid) endMeeting(tid);
      } else if (ev.type === "reply") {
        if (ev.from && AG[ev.from]) startReply(ev.from, ev.text || "");
      } else {
        /* request/chat/anything else with from/to/text. */
        if (ev.from && ev.to && AG[ev.from] && AG[ev.to]) startRequest(ev.from, ev.to, ev.text || "");
      }
    });
  }

  /* ------------------------------------------------------------------
     Hover (tooltip) + bubble overlay.
  ------------------------------------------------------------------ */
  const tipEl = document.createElement("div");
  tipEl.className = "fl-tip"; tipEl.hidden = true;
  tipEl.innerHTML = "<b></b><span></span><span></span>";
  overlay.appendChild(tipEl);
  const ray = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let hovered = null;
  function hoverAgent(a, ev) {
    hovered = a;
    if (!a) { tipEl.hidden = true; ring.visible = false; return; }
    const cur = a.moving ? "Walking" : (a.activity || STATUS[a.status].hint);
    tipEl.children[0].textContent = a.name;
    tipEl.children[1].textContent = STATUS[a.status].word + " · " + (RM[a.def.room]?.name || "");
    tipEl.children[2].textContent = cur;
    tipEl.hidden = false; placeTip(ev);
  }
  function placeTip(ev) {
    if (tipEl.hidden) return;
    const r = container.getBoundingClientRect();
    let x, y;
    if (ev) { x = ev.clientX - r.left + 14; y = ev.clientY - r.top + 14; }
    else if (hovered) {
      const v = hovered.g.position.clone(); v.y = 1.2; v.project(camera);
      x = (v.x * 0.5 + 0.5) * r.width + 18; y = (-v.y * 0.5 + 0.5) * r.height + 6;
    } else return;
    const w = tipEl.offsetWidth, h = tipEl.offsetHeight;
    if (x + w > r.width - 6) x = Math.max(6, x - w - 30);
    if (y + h > r.height - 6) y = Math.max(6, y - h - 26);
    tipEl.style.transform = `translate(${Math.round(x)}px,${Math.round(y)}px)`;
  }
  function onMove(ev) {
    const r = canvas.getBoundingClientRect();
    mouse.x = ((ev.clientX - r.left) / r.width) * 2 - 1;
    mouse.y = -((ev.clientY - r.top) / r.height) * 2 + 1;
    ray.setFromCamera(mouse, camera);
    const h = ray.intersectObjects(hits, false);
    const a = h.length ? h[0].object.userData.agent : null;
    if (a !== hovered) hoverAgent(a, ev); else placeTip(ev);
    canvas.style.cursor = a ? "pointer" : "default";
  }
  function onLeave() { hoverAgent(null); }
  canvas.addEventListener("pointermove", onMove);
  canvas.addEventListener("pointerdown", onMove);
  canvas.addEventListener("pointerleave", onLeave);

  function updateBubbles(w, h) {
    const v = new THREE.Vector3();
    AGL.forEach((a) => {
      if (a.bubble) {
        if (!a.bubbleEl) { a.bubbleEl = document.createElement("div"); a.bubbleEl.className = "fl-bubble"; overlay.appendChild(a.bubbleEl); }
        if (a.bubbleEl.textContent !== a.bubble) a.bubbleEl.textContent = a.bubble;
        v.copy(a.g.position); v.y = 2.7; v.project(camera);
        const x = (v.x * 0.5 + 0.5) * w, y = (-v.y * 0.5 + 0.5) * h;
        a.bubbleEl.style.display = "block";
        a.bubbleEl.style.transform = `translate(${Math.round(x)}px,${Math.round(y)}px) translate(-50%,-100%)`;
      } else if (a.bubbleEl && a.bubbleEl.style.display !== "none") {
        a.bubbleEl.style.display = "none";
      }
    });
  }

  /* ------------------------------------------------------------------
     Sizing + loop.
  ------------------------------------------------------------------ */
  let W = 0, H = 0, dist = 38;
  function resize() {
    const r = container.getBoundingClientRect();
    const w = Math.max(200, Math.floor(r.width));
    const h = Math.max(160, Math.floor(r.height));
    if (w === W && h === H) return;
    W = w; H = h;
    renderer.setSize(w, h, false);
    camera.aspect = w / h; camera.updateProjectionMatrix();
    const tf = Math.tan(camera.fov * Math.PI / 360);
    dist = Math.max(23.2 / (tf * camera.aspect), 8.4 / tf);
  }
  const dirV = new THREE.Vector3(0, 0.78, 0.63).normalize();
  function placeCamera(t) {
    const sway = reduce ? 0 : Math.sin(t * 0.18) * 1.4;
    camera.position.set(target.x + sway, target.y + dirV.y * dist, target.z + dirV.z * dist);
    camera.lookAt(target.x, target.y, target.z);
  }
  let lastT = 0, raf = 0, running = false;
  function frame(now) {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(0.05, (now - lastT) / 1000); lastT = now; T += dt;
    for (let i = 0; i < AGL.length; i++) { stepAgent(AGL[i], dt); pose(AGL[i], T); }
    ledMeshes.forEach((l) => { l.visible = reduce || (Math.floor(T * 2) % 2 === 0); });
    const prog = reduce ? 1 : Math.min(1, T / 1.6);
    edgeLines.forEach((e) => { e.geo.setDrawRange(0, Math.ceil(e.total * prog)); });
    if (hovered) {
      ring.visible = true;
      ring.position.x = hovered.g.position.x;
      ring.position.z = hovered.g.position.z;
      placeTip(null);
    } else { ring.visible = false; }
    resize(); placeCamera(T); renderer.render(scene, camera); updateBubbles(W, H);
  }
  function start() {
    if (running) return;
    running = true; lastT = performance.now();
    raf = requestAnimationFrame(frame);
  }
  function stop() {
    running = false;
    if (raf) { cancelAnimationFrame(raf); raf = 0; }
  }

  /* ResizeObserver on the container. */
  let ro = null;
  if (typeof ResizeObserver !== "undefined") {
    ro = new ResizeObserver(() => { W = 0; resize(); });
    ro.observe(container);
  }
  resize();

  /* Pre-populate the 12 canonical agents so the scene is never empty
     before the first /api/office/live tick returns. */
  Object.keys(DEFS).forEach((name) => spawnAgent(name, RM[DEFS[name].room]?.name));

  /* Optional debug probe (?__floorDebug=1). */
  let debugAttached = false;
  try {
    if (typeof window !== "undefined" && window.location && window.location.search.indexOf("__floorDebug=1") !== -1) {
      window.__floor3d = {
        statusOf: (n) => AG[n] && AG[n].status,
        posOf:    (n) => (AG[n] ? [AG[n].g.position.x, AG[n].g.position.z] : null),
        agents:   () => Object.keys(AG),
        _enqueue: (evs) => enqueue(evs),
        _setAgents: (ags) => setAgents(ags),
        _snapshot: (ags) => snapshot(ags),
        _queued: (n) => AG[n] ? AG[n].q.length : 0,
      };
      debugAttached = true;
    }
  } catch {}

  /* ------------------------------------------------------------------
     Destroy.
  ------------------------------------------------------------------ */
  function destroy() {
    stop();
    if (ro) { try { ro.disconnect(); } catch {} ro = null; }
    canvas.removeEventListener("pointermove", onMove);
    canvas.removeEventListener("pointerdown", onMove);
    canvas.removeEventListener("pointerleave", onLeave);
    /* Dispose geometries + materials. */
    scene.traverse((o) => {
      if (o.geometry) { try { o.geometry.dispose(); } catch {} }
      if (o.material) {
        const mats = Array.isArray(o.material) ? o.material : [o.material];
        mats.forEach((m) => { if (m.map) { try { m.map.dispose(); } catch {} } try { m.dispose(); } catch {} });
      }
    });
    /* Edge geometries. */
    edgeLines.forEach((e) => { try { e.geo.dispose(); } catch {} });
    /* Label textures. */
    labels.forEach((l) => { try { l.tex.dispose(); } catch {} });
    /* Marker textures. */
    Object.keys(markerTex).forEach((k) => { try { markerTex[k].dispose(); } catch {} delete markerTex[k]; });
    /* Overlay DOM. */
    while (overlay.firstChild) overlay.removeChild(overlay.firstChild);
    /* Renderer + force context loss. */
    try { renderer.dispose(); } catch {}
    try {
      const gl = renderer.getContext && renderer.getContext();
      const ext = gl && gl.getExtension && gl.getExtension("WEBGL_lose_context");
      if (ext) ext.loseContext();
    } catch {}
    if (debugAttached) { try { delete window.__floor3d; } catch {} }
    AGL.length = 0;
    Object.keys(AG).forEach((k) => delete AG[k]);
  }

  return {
    start, stop,
    /* Floor.jsx calls resize(width); 3D engine recomputes from the
       container via ResizeObserver, so this is intentionally a no-op. */
    resize: () => { W = 0; resize(); },
    snapshot, setAgents, enqueue,
    destroy,
  };
}
